import { Button, Link, Modal, Paper, Typography } from "@material-ui/core";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { useStore } from "store/StoreConfigs";
import { uploadAreaStyles } from "./UploadAreaStyles";
import { ReactComponent as Upload } from "icons/Upload.svg";
import { services } from "api/serviceConfig";
import { EventAttachment } from "models/thorEventModels/EventAttachment";
import { ReactComponent as AssignEventMenuCloseBtn } from "icons/AssignEventMenuCloseBtn.svg";

export default observer(function UploadArea() {
    const { thorEventViewStore, identityStore } = useStore();
    const [uploadState, setUploadState] = useState(new Map());
    const classes = uploadAreaStyles();

    const [drag, setDrag] = useState(false);

    const dragStartHandler = (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        setDrag(true);
    };

    const dragLeaveHandler = (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        setDrag(false);
    };

    const uploadStatusCallback = (data: ProgressEvent, attachmentId: number) => {
        if (!uploadState.has(attachmentId)) {
            const mapData = uploadState.set(attachmentId, (data.loaded * 100) / data.total);
            setUploadState(new Map(mapData));
        } else {
            setUploadState(new Map(uploadState.set(attachmentId, (data.loaded * 100) / data.total)));
        }
    };

    const uploadToS3AndConfirm = async (file: File) => {
        const urlResponse = await services.Thor.getUploadPresignedUrl(
            {
                fileName: file.name,
                eventId: thorEventViewStore.eventDetails.eventId,
                customerId: identityStore.currentCustomerId,
                contentType: file.type,
            },
            file
        );
        thorEventViewStore.attachedFiles.push(urlResponse.data.eventAttachment);
        const resp = await services.Thor.uploadToS3WithPresignedUrl(
            file,
            urlResponse.data.url,
            (event: ProgressEvent) => uploadStatusCallback(event, urlResponse.data.eventAttachment.id)
        );
        if (resp.status !== 200) {
            console.log(resp);
            return;
        }
        await services.Thor.confirmUpload({
            attachmentId: urlResponse.data.eventAttachment.id,
            customerId: identityStore.currentCustomerId,
        });
    };

    const uploadFiles = async (files: File[], processedFiles: EventAttachment[]) => {
        for (const file of files) {
            uploadToS3AndConfirm(file);
        }
    };

    const processAttachedFiles = (files: File[]) => {
        const newFiles = new Map(files.map((f) => [f.name, f]));
        const existedFilesMap = new Map(thorEventViewStore.attachedFiles.map((f) => [f.fileName, f]));
        const result = Array.from(existedFilesMap.values());
        const filesToRequest: File[] = [];
        newFiles.forEach((v, k) => {
            if (!existedFilesMap.has(k)) {
                filesToRequest.push(v);
            }
        });
        uploadFiles(filesToRequest, result);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        e.preventDefault();
        if (e.target.files === null) {
            return;
        }
        processAttachedFiles(Array.from(e.target.files));
        e.target.value = "";
    };

    const onDropHandler = (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        const files: File[] = Array.prototype.slice.call(e.dataTransfer.files);

        if (files === null || files.length === 0) {
            return;
        }
        processAttachedFiles(files);
        setDrag(false);
    };

    const onRemoveFile = async (file: EventAttachment) => {
        await services.Thor.deleteEventAttachment(file.id, identityStore.currentCustomerId);
        const indexOf = thorEventViewStore.attachedFiles.findIndex((f) => f.id === file.id);
        thorEventViewStore.attachedFiles.splice(indexOf, 1);
    };

    const downloadFile = async (file: EventAttachment) => {
        const resp = await services.Thor.getDownloadPresignedUrl(file.id, identityStore.currentCustomerId);
        const blobResponse = await services.Thor.downloadFromS3WithPresignedUrl(resp.data.url);
        if (blobResponse.status !== 200) {
            return;
        }
        const link = window.URL.createObjectURL(blobResponse.data);
        const stylesheet = document.createElement("a");
        stylesheet.href = link;
        stylesheet.download = resp.data.eventAttachment.fileName;
        stylesheet.click();
    };

    return (
        <Modal
            open={thorEventViewStore.isFileAttachModalOpen}
            onClose={() => {
                thorEventViewStore.setFileAttachModalOpen(false);
            }}
            className={classes.modal}
        >
            <Paper className={classes.main}>
                <AssignEventMenuCloseBtn
                    className={classes.closeIcon}
                    onClick={() => {
                        thorEventViewStore.setFileAttachModalOpen(false);
                    }}
                />
                <Typography variant="h3" className={classes.modalTitle}>
                    Add a New Document
                </Typography>
                <Typography className={classes.modalSubtitle}>
                    Choose a file from your device <br /> you’d like to attach to this event.
                </Typography>
                <div className={classes.dropArea}>
                    {drag ? (
                        <div
                            className={classes.dragged}
                            onDragStart={(e) => dragStartHandler(e)}
                            onDragLeave={(e) => dragLeaveHandler(e)}
                            onDragOver={(e) => dragStartHandler(e)}
                            onDrop={(e) => onDropHandler(e)}
                        >
                            <Typography>Drop </Typography>
                        </div>
                    ) : (
                        <div
                            className={classes.drag}
                            onDragStart={(e) => dragStartHandler(e)}
                            onDragLeave={(e) => dragLeaveHandler(e)}
                            onDragOver={(e) => dragStartHandler(e)}
                        >
                            <Upload />
                            <Typography>Drag and Drop here</Typography>
                        </div>
                    )}
                    <div className={classes.fileInput}>
                        <form>
                            <label className={classes.fileLabel}>
                                Select a file
                                <input
                                    onChange={(e) => handleInputChange(e)}
                                    type="file"
                                    multiple
                                    style={{ display: "none" }}
                                />
                                <br />
                            </label>
                        </form>
                    </div>
                    <div className={classes.resultDocumentsList}>
                        {thorEventViewStore.attachedFiles && (
                            <Typography>
                                {thorEventViewStore.attachedFiles.map((x, index) => (
                                    <Typography key={x.fileName + index}>
                                        <Link
                                            href="#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                downloadFile(x);
                                            }}
                                        >
                                            {x.fileName}
                                        </Link>
                                        <Button
                                            onClick={() => {
                                                onRemoveFile(x);
                                            }}
                                            style={
                                                uploadState.get(x.id) > 0 && uploadState.get(x.id) < 100
                                                    ? {
                                                          background:
                                                              "linear-gradient(to right, red " +
                                                              uploadState.get(x.id) +
                                                              "%, green 0%)",
                                                      }
                                                    : {}
                                            }
                                        >
                                            x
                                        </Button>
                                    </Typography>
                                ))}
                            </Typography>
                        )}
                    </div>
                </div>
            </Paper>
        </Modal>
    );
});
